import { useCallback, useContext, useId, useMemo } from "react";
import SaveGuardEnabledIdsContext from "@coworker/apprestructured/src/save-guard/SaveGuardProvider/SaveGuardEnabledIdsContext";

/**
 * Read, enable or disable a save guard.
 * @param paramId the save guard id, uniquely generated if not provided
 */
function useSaveGuard(paramId?: string) {
  const generatedId = useId();
  const id = paramId ?? generatedId;

  const [saveGuardEnabledIds, setSaveGuardEnabledIds] = useContext(
    SaveGuardEnabledIdsContext
  );

  const saveGuardIsEnabled = useMemo(() => {
    return saveGuardEnabledIds.has(id);
  }, [id, saveGuardEnabledIds]);

  const enableSaveGuard = useCallback(() => {
    setSaveGuardEnabledIds((prev) => {
      if (prev.has(id)) {
        return prev;
      } else {
        return new Set([...prev, id]);
      }
    });
  }, [id, setSaveGuardEnabledIds]);

  const disableSaveGuard = useCallback(() => {
    setSaveGuardEnabledIds((prev) => {
      if (prev.has(id)) {
        return new Set([...prev].filter((prevId) => prevId !== id));
      } else {
        return prev;
      }
    });
  }, [id, setSaveGuardEnabledIds]);

  return useMemo(() => {
    return { saveGuardIsEnabled, enableSaveGuard, disableSaveGuard };
  }, [disableSaveGuard, enableSaveGuard, saveGuardIsEnabled]);
}

export default useSaveGuard;
